/**
 * The unsent "Ask for stock" / "Send back" list, kept on this tablet per
 * person (pilot fix, Batch 5 lane tablet). Leaving the screen used to throw the
 * list away; now it comes back with a small "Restored your unsent list" note,
 * and is cleared once it has been sent (or saved to send).
 *
 * Storage can be missing or throw (private mode, blocked site data): every
 * read and write is wrapped, and the list then just lives for the screen.
 */
import type { KVStorage } from './offlineQueue'
import type { RequisitionKind } from './types'

export interface AskDraft {
  lines: { id: string; qty: number }[]
  neededBy: string
  notes: string
}

export const EMPTY_ASK_DRAFT: AskDraft = { lines: [], neededBy: '', notes: '' }

export function askDraftKey(kind: RequisitionKind, userId: string): string {
  return `bhookly.inventoryFlow.askDraft.v1.${kind}.${userId || 'anon'}`
}

function defaultStorage(): KVStorage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

export function isEmptyAskDraft(d: AskDraft): boolean {
  return d.lines.length === 0 && !d.neededBy && !d.notes.trim()
}

export function loadAskDraft(key: string, storage: KVStorage | null = defaultStorage()): AskDraft {
  try {
    const raw = storage?.getItem(key)
    if (!raw) return EMPTY_ASK_DRAFT
    const p = JSON.parse(raw) as Partial<AskDraft>
    const lines = Array.isArray(p.lines)
      ? p.lines.filter((l): l is { id: string; qty: number } => !!l && typeof l.id === 'string' && typeof l.qty === 'number' && Number.isFinite(l.qty))
      : []
    return { lines, neededBy: typeof p.neededBy === 'string' ? p.neededBy : '', notes: typeof p.notes === 'string' ? p.notes : '' }
  } catch {
    return EMPTY_ASK_DRAFT
  }
}

/** Saves the list; an empty list removes the entry. */
export function saveAskDraft(key: string, d: AskDraft, storage: KVStorage | null = defaultStorage()): void {
  try {
    if (!storage) return
    if (isEmptyAskDraft(d)) {
      if ('removeItem' in storage && typeof (storage as Storage).removeItem === 'function') (storage as Storage).removeItem(key)
      else storage.setItem(key, '')
      return
    }
    storage.setItem(key, JSON.stringify(d))
  } catch {
    /* storage full or blocked — the list lives for this screen only */
  }
}

/** One scanned pack onto the list: a new line, or a pack more on its line. */
export function addPackToDraft(lines: AskDraft['lines'], itemId: string, packQty: number): AskDraft['lines'] {
  const pack = packQty > 0 ? packQty : 1
  const i = lines.findIndex((l) => l.id === itemId)
  if (i < 0) return [...lines, { id: itemId, qty: pack }]
  return lines.map((l, j) => (j === i ? { ...l, qty: Math.round((l.qty + pack) * 1e5) / 1e5 } : l))
}

/** How many matching items the item list shows at a time ("Show more" adds this many). */
export const ITEM_PAGE = 200
